/**
 * Integrytas Group - Base App (MOBILE)
 * Page: APR.INBOX — mobile-native "My Approvals" inbox.
 *
 * Touch-first counterpart of src/pages/admin/MyApprovalsPage.tsx. A DISTINCT
 * presentation (dense card list + facet chips + bottom-sheet detail) over the
 * SAME data/logic layer as the desktop page — never the desktop grid re-hosted
 * (§16):
 *   - reads via apiDb: apr_step (my pending steps) + apr_request (PENDING
 *     requests), plus a csm_user_master lookup to resolve the requester id to a
 *     name (§17.B4 — never render a raw id).
 *   - decisions route through the APR engine RPCs exactly like the desktop page
 *     (apr_decide / apr_request_changes). Those RPCs ARE the sanctioned write
 *     path for approvals: the engine applies quorum, level advancement and the
 *     staged-write commit through the Common Routine (§2). They are RPC calls,
 *     not a direct apiDb insert/update/delete — writing apr_step.decision by hand
 *     via routedUpdate would bypass all of that and is intentionally NOT done.
 *
 * Admin-only override/reassign (desktop E1/E4) are deliberately omitted here —
 * they rely on password prompts and are not part of the mobile approver flow.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Inbox, ClipboardCheck, Check, Ban, Undo2, MinusCircle, UserX, X } from 'lucide-react';
import { apiDb } from '../../lib/api_client';
import { useFormatDateTime } from '../../lib/format_date';
import { useApprovalInbox, type InboxItem, type RequestRow } from '../lib/use_approval_inbox';
import { MobilePageHeader, MobileStatStrip, MobileContentCard, MobileCardRow, MobileDetailRow, MobileFilterChips, MobileStatusPill } from '../components';
import type { MobilePageComponentProps } from '../mobile_page_components';

/** Shape returned by the APR engine decision RPCs. */
interface AprDecisionResult {
  ok?: boolean;
  error_code?: string;
  state?: string;
}

type ApproverDecision = 'APPROVE' | 'REJECT' | 'ABSTAIN' | 'RECUSE';

export function MobileMyApprovalsPage({ session }: MobilePageComponentProps) {
  const { t } = useTranslation();
  const formatDateTime = useFormatDateTime();
  // Inbox list + count come from the shared hook, so the Approvals tab badge in
  // the shell can never disagree with this list (both read useApprovalInbox).
  const { items, busy, error: loadError, reload } = useApprovalInbox(session);

  const [filter, setFilter] = useState('');
  const [entityFacet, setEntityFacet] = useState<string>('all');
  const [selected, setSelected] = useState<InboxItem | null>(null);
  const [comment, setComment] = useState('');
  const [actionBusy, setActionBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  // Show the load error or the last action error, whichever is set (errors-only, §17.B7).
  const error = actionError ?? loadError;

  // Reset the comment field whenever a different item is opened.
  useEffect(() => { setComment(''); }, [selected]);

  const entityOptions = useMemo(() => {
    const uniq = [...new Set(items.map((it) => it.request.entity_name).filter(Boolean))].sort();
    return uniq;
  }, [items]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return items.filter(({ request, requesterName }) => {
      if (entityFacet !== 'all' && request.entity_name !== entityFacet) return false;
      if (!q) return true;
      return (
        request.entity_name.toLowerCase().includes(q) ||
        (request.record_id ?? '').toLowerCase().includes(q) ||
        requesterName.toLowerCase().includes(q)
      );
    });
  }, [items, filter, entityFacet]);

  const levelLabel = (level: number | string) =>
    t('mobile.approvals.level', { level, defaultValue: `Level ${level}` });
  const sourceLabel = (source: string | null) =>
    source ? t(`mobile.approvals.source.${source}`, source) : t('common.none', 'None');
  const recordLabel = (request: RequestRow) =>
    request.record_id != null
      ? `#${request.record_id}`
      : t('mobile.approvals.staged', 'Staged {{op}}', { op: request.staged_operation ?? '—' });

  // Decide via the APR engine RPC (mirrors the desktop page). Returns success.
  const decide = async (item: InboxItem, decision: ApproverDecision): Promise<boolean> => {
    const { data, error: e } = await apiDb.rpc<AprDecisionResult | null>('apr_decide', {
      p_request_id: item.request.approval_request_id,
      p_decision: decision,
      p_comment: comment.trim() || null,
    });
    if (e) throw e;
    if (data && data.ok === false) throw new Error(data.error_code ?? 'APR.FAILED');
    return true;
  };

  const requestChanges = async (item: InboxItem): Promise<boolean> => {
    const { data, error: e } = await apiDb.rpc<AprDecisionResult | null>('apr_request_changes', {
      p_request_id: item.request.approval_request_id,
      p_comment: comment.trim() || null,
    });
    if (e) throw e;
    if (data && data.ok === false) throw new Error(data.error_code ?? 'APR.FAILED');
    return true;
  };

  const runAction = async (fn: () => Promise<boolean>) => {
    setActionBusy(true);
    setActionError(null);
    try {
      await fn();
      setSelected(null);
      await reload();
    } catch (e) {
      setActionError((e as Error).message);
    } finally {
      setActionBusy(false);
    }
  };

  return (
    <section className="m-screen">
      <MobilePageHeader
        title={t('mobile.approvals.title', 'My Approvals')}
        description={t('mobile.approvals.description', 'Requests awaiting your decision.')}
      />

      <MobileStatStrip
        stats={[
          { value: items.length, label: t('mobile.approvals.kpi_pending', 'Pending') },
          { value: entityOptions.length, label: t('mobile.approvals.kpi_entities', 'Entities') },
        ]}
      />

      <div className="m-searchbox">
        <Search />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('mobile.approvals.search', 'Search approvals')}
          aria-label={t('mobile.approvals.search', 'Search approvals')}
        />
      </div>

      {entityOptions.length > 1 && (
        <MobileFilterChips
          chips={[
            { value: 'all', label: t('mobile.approvals.all', 'All') },
            ...entityOptions.map((name) => ({ value: name, label: name })),
          ]}
          isActive={(v) => entityFacet === v}
          onSelect={(v) => setEntityFacet(v)}
        />
      )}

      {error && (
        <MobileContentCard>
          <div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div>
        </MobileContentCard>
      )}

      {filtered.length === 0 && !busy ? (
        <MobileContentCard>
          <div className="m-empty"><Inbox /> {t('mobile.approvals.none', 'Nothing awaiting your approval')}</div>
        </MobileContentCard>
      ) : (
        <MobileContentCard list>
          {filtered.map((it) => (
            <MobileCardRow
              key={it.step.approval_step_id}
              lead={<ClipboardCheck />}
              leadTone="amber"
              title={it.request.entity_name}
              subtitle={`${recordLabel(it.request)} · ${levelLabel(it.request.current_level)} · ${it.requesterName}`}
              trailing={<MobileStatusPill tone="wait">{t('mobile.approvals.pending', 'Pending')}</MobileStatusPill>}
              chevron
              onClick={() => setSelected(it)}
            />
          ))}
        </MobileContentCard>
      )}

      {/* Detail bottom-sheet */}
      <div className={'m-scrim' + (selected ? ' m-open' : '')} onClick={() => { if (!actionBusy) setSelected(null); }} />
      <div className={'m-sheet' + (selected ? ' m-open' : '')} aria-label={t('mobile.approvals.detail', 'Approval detail')}>
        <div className="m-grabber" />
        {selected && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><ClipboardCheck size={20} /></span>
              <span>
                <div className="m-nm">{selected.request.entity_name}</div>
                <div className="m-em">{recordLabel(selected.request)}</div>
              </span>
            </div>

            <MobileContentCard>
              <MobileDetailRow
                label={t('mobile.approvals.level_label', 'Level')}
                value={levelLabel(selected.request.current_level)}
              />
              <MobileDetailRow
                label={t('mobile.approvals.source', 'Assigned via')}
                value={sourceLabel(selected.step.approver_source)}
              />
              <MobileDetailRow
                label={t('mobile.approvals.requested_by', 'Requested by')}
                value={selected.requesterName}
              />
              <MobileDetailRow
                label={t('mobile.approvals.requested_at', 'Requested at')}
                value={formatDateTime(selected.request.requested_at)}
              />
              {selected.request.staged_operation && (
                <MobileDetailRow
                  label={t('mobile.approvals.operation', 'Operation')}
                  value={t(`mobile.approvals.op.${selected.request.staged_operation}`, selected.request.staged_operation)}
                />
              )}
            </MobileContentCard>

            <div className="m-fieldcard">
              <div className="m-formrow">
                <label htmlFor="m-apr-comment">{t('mobile.approvals.comment', 'Comment (optional)')}</label>
                <textarea
                  id="m-apr-comment"
                  className="m-inp"
                  rows={3}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder={t('mobile.approvals.comment_placeholder', 'Add a note for the requester…')}
                />
              </div>
            </div>

            {/* Secondary decisions */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginTop: 12 }}>
              <button
                type="button"
                className="m-btn m-btn-ghost"
                disabled={actionBusy}
                onClick={() => void runAction(() => requestChanges(selected))}
              >
                <Undo2 size={16} />
                {t('mobile.approvals.request_changes', 'Changes')}
              </button>
              <button
                type="button"
                className="m-btn m-btn-ghost"
                disabled={actionBusy}
                onClick={() => void runAction(() => decide(selected, 'ABSTAIN'))}
              >
                <MinusCircle size={16} />
                {t('mobile.approvals.abstain', 'Abstain')}
              </button>
              <button
                type="button"
                className="m-btn m-btn-ghost"
                disabled={actionBusy}
                onClick={() => void runAction(() => decide(selected, 'RECUSE'))}
              >
                <UserX size={16} />
                {t('mobile.approvals.recuse', 'Recuse')}
              </button>
            </div>

            {/* Primary decisions */}
            <div className="m-actionbar">
              <button
                type="button"
                className="m-btn m-btn-primary"
                disabled={actionBusy}
                onClick={() => void runAction(() => decide(selected, 'APPROVE'))}
              >
                <Check size={16} />
                {t('mobile.approvals.approve', 'Approve')}
              </button>
              <button
                type="button"
                className="m-btn m-btn-danger"
                disabled={actionBusy}
                onClick={() => void runAction(() => decide(selected, 'REJECT'))}
              >
                <Ban size={16} />
                {t('mobile.approvals.reject', 'Reject')}
              </button>
              <button
                type="button"
                className="m-btn m-btn-ghost"
                disabled={actionBusy}
                onClick={() => setSelected(null)}
                aria-label={t('mobile.close', 'Close')}
              >
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
